import {
  ContentBanner1 as FuseContentBanner1,
  ContentBanner1Props as FuseContentBanner1Props,
} from "@tvg/fuse-design/content-banners";
import { GridContainer, GridItem, GridRow } from "@tvg/fuse-design/responsive-grid";
import { SpacingWrapper } from "@tvg/fuse-design/spacing-wrapper";
import useUser from "client/hooks/useUser";
import { isPersonalisedConfig } from "client/utils/amplience";
import getEventHandler from "client/utils/eventHandler";
import buildGoogleAnalyticsTag from "client/utils/getDataAnalyticsId";
import getImageSources from "client/utils/getImageSources";
import userMeetsPersonalizationCriteria from "client/utils/userMeetsPersonalizationCriteria";
import React, { useMemo } from "react";
import styled from "styled-components";
import { AmpContentBannerConfig, AmpInGridBannerConfig } from "types/amplience";
import { Brand } from "types/common";
import { getBrandColourComponent, getImageSizeConfig, toContentBannerVariant } from "../InGridBanners/utils";
import useCTAEvents from "../../hooks/useCTAEvents";

export interface ContentBanner1Props extends FuseContentBanner1Props {
  config: AmpInGridBannerConfig;
  brand: Brand;
  inGrid?: boolean;
}

const StyledGridContainer = styled(GridContainer)`
  && {
    padding: 0;
  }
`;

const ContentBanner1: React.FC<ContentBanner1Props> = ({ config, brand, inGrid = false, ...props }) => {
  const user = useUser();

  const configProps = useMemo<FuseContentBanner1Props | null>(() => {
    const isPersonalised = isPersonalisedConfig(config);
    if (isPersonalised && !userMeetsPersonalizationCriteria(user, config.personalization)) return null;

    const c = !isPersonalised ? config : (config.component as AmpContentBannerConfig);

    const primaryLink = c.cta?.[0];
    const secondaryLink = c.cta?.[1];
    const imageSize = getImageSizeConfig(c.imageSize);

    return {
      variant: toContentBannerVariant(c.variant),

      image: c.image && {
        ...getImageSources(c.image, {
          brand,
          w: 740,
          qlt: "default",
          templates: ["poi"],
          aspect: imageSize.aspect,
        }),
        isLazy: true,
        size: imageSize.name,
      },

      textContent: {
        heading: c.heading,
        headingTag: "h2",
        subHeading: c.subheading,
        subHeadingTag: "h3",
        addBrandColour: getBrandColourComponent(c.addBrandColour),
        switchSubHeadingPosition: c.switchSubHeadingPosition,
        paragraph: c.paragraph,
        legalText: c.terms,
        representative: c.representative,
      },

      link: {
        text: primaryLink?.text,
        href: primaryLink?.url,
        onClick: primaryLink?.publishEvent ? getEventHandler(primaryLink.publishEvent) : undefined,
      },

      lLayout: inGrid ? "v2" : "v1",
      secondaryLink: {
        text: secondaryLink?.text,
        href: secondaryLink?.url,
        onClick: secondaryLink?.publishEvent ? getEventHandler(secondaryLink.publishEvent) : undefined,
      },
    };
  }, [brand, config, inGrid, user]);

  const dataAnalyticsId = useMemo(() => {
    const heading = configProps?.textContent?.heading;
    const subHeading = configProps?.textContent?.subHeading;
    const primLinkText = configProps?.link?.text;

    return buildGoogleAnalyticsTag(
      "ContentBanner1",
      heading || subHeading
        ? `${heading || subHeading} - ${primLinkText} - ${configProps?.variant}`
        : `${primLinkText || configProps?.secondaryLink?.text || configProps?.variant}`,
    );
  }, [
    configProps?.link?.text,
    configProps?.secondaryLink?.text,
    configProps?.textContent?.heading,
    configProps?.textContent?.subHeading,
    configProps?.variant,
  ]);

  const ctaEvents = useCTAEvents(isPersonalisedConfig(config) ? config.component.cta : config.cta);

  if (!configProps) return null;
  return (
    <SpacingWrapper marginBottom="l">
      <StyledGridContainer>
        <GridRow>
          <GridItem xxs="12">
            <FuseContentBanner1
              dataTestId="content-content-banner"
              dataAnalyticsId={dataAnalyticsId}
              data-contentid={config._meta.deliveryId}
              data-cta-event={ctaEvents}
              lLayout="v1" // based on designs for browse pages
              {...props}
              {...configProps}
            />
          </GridItem>
        </GridRow>
      </StyledGridContainer>
    </SpacingWrapper>
  );
};
export default React.memo(ContentBanner1);
